/*
* === MessageCenterPage.js (Final - Live Sync & Sender Details) ===
*/
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
    Container, Typography, Box, Grid, Paper, List, ListItemButton, 
    ListItemText, Divider, TextField, Button, CircularProgress, Alert, 
    InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, 
    Avatar, ListItemAvatar, Tooltip, Chip 
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useSearchParams, useLocation } from 'react-router-dom';

// --- Date & Timezone Corrector ---
const getSafeDate = (obj) => {
    if (!obj) return new Date();
    const rawDate = obj.Timestamp || obj.timestamp || obj.CreatedAt || obj.createdAt || obj.SentAt || obj.sentAt || obj.LastMessageAt || obj.lastMessageAt;

    if (!rawDate) return new Date();

    let dateString = typeof rawDate === 'string' ? rawDate : rawDate.toString();
    if (dateString.endsWith('Z')) {
        dateString = dateString.slice(0, -1);
    }
    return new Date(dateString);
};

// --- Helper Functions ---
const formatTimestamp = (dateInput) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    const now = new Date();
    const isToday = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    const isYesterday = date.getDate() === now.getDate() - 1 && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    if (isToday) return date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
    if (isYesterday) return 'Yesterday';
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// 🚨 THE FIX: Bulletproof Unread Parser
const getSafeUnreadCount = (thread) => {
    if (!thread) return 0;
    // Catch every possible alias your backend might use
    const val = thread.UnreadCount ?? thread.unreadCount ?? thread.unread_count ?? thread.unread ?? 0;
    return parseInt(val, 10);
};

const ReadReceipt = ({ isSender, isRead }) => {
    if (!isSender) return null;
    return Boolean(isRead) 
        ? <DoneAllIcon fontSize="small" color="secondary" sx={{ ml: 0.5, height: '14px' }} />
        : <CheckIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5, height: '14px' }} />;
};

const DateSeparator = ({ date }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', fontWeight: 'bold' }}>{date}</Typography>
        <Divider sx={{ flexGrow: 1 }} />
    </Box>
);

const renderMessageBody = (text) => {
    if (!text) return '';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) => {
        if (part.match(urlRegex)) {
            let displayText = 'Link';
            try {
                const urlObj = new URL(part);
                const pathSegments = urlObj.pathname.split('/');
                const fileName = pathSegments[pathSegments.length - 1];
                if (fileName) displayText = fileName.substring(0, 20) + '...';
            } catch (e) {}
            return (
                <a href={part} target="_blank" rel="noopener noreferrer" key={index} style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all', fontWeight: 'bold' }}>
                    {displayText}
                </a>
            );
        }
        return part;
    });
};

const groupMessagesByDate = (messages) => {
    const groups = [];
    let lastDate = null;
    messages.forEach((msg) => {
        const dateObj = getSafeDate(msg);
        const dateStr = dateObj.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' });
        const now = new Date();
        let displayDate = dateStr;
        if (dateObj.toDateString() === now.toDateString()) displayDate = "Today";
        else if (dateObj.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString()) displayDate = "Yesterday";
        if (displayDate !== lastDate) {
            groups.push({ type: 'separator', date: displayDate, id: `sep-${msg.Id}` });
            lastDate = displayDate;
        }
        groups.push({ type: 'message', data: msg });
    });
    return groups;
};

// --- ChatView Component ---
const ChatView = ({ thread, token, userId, onMessageSent, refreshUnreadCount, onThreadRead, onBack }) => {
    // 🚨 1. React Hooks MUST be placed right at the top of the component!
    const location = useLocation();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const scrollContainerRef = useRef(null);
    const [hasMarkedAsRead, setHasMarkedAsRead] = useState(false);
    const [senderContext, setSenderContext] = useState(null);

    // 🚨 2. Listen for 'draft' parameter from navigation
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const draftText = params.get('draft');
        if (draftText) {
            setNewMessage(draftText);
        }
    }, [location.search]);

    const fetchMessages = useCallback(async () => {
        if (!thread || thread.isNewThread) {
            setMessages([]);
            setSenderContext(null);
            return;
        }
        setLoading(true);
        try {
            const params = { otherUserId: thread.OtherUserId || thread.otherUserId };
            const appId = thread.ApplicationId || thread.applicationId;
            if (appId) params.applicationId = appId;

            const response = await api.get('/messages/thread', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            
            if (Array.isArray(response.data)) {
                 setMessages(response.data);
                 if (thread.OtherPartyEmail || thread.OtherPartyRole) {
                     setSenderContext({ 
                         email: thread.OtherPartyEmail, 
                         role: thread.OtherPartyRole 
                     });
                 }
            }
        } catch (err) { setError('Failed to load messages.'); } 
        finally { setLoading(false); }
    }, [thread, token]);

    useEffect(() => {
        if (thread) {
            fetchMessages();
            setHasMarkedAsRead(false); 
        }
    }, [thread, fetchMessages]);

    useEffect(() => {
        const count = getSafeUnreadCount(thread);
        if (thread && !loading && !hasMarkedAsRead && !thread.isNewThread && count > 0) {
            api.put('/messages/read', 
                { applicationId: thread.ApplicationId, otherUserId: thread.OtherUserId }, 
                { headers: { Authorization: `Bearer ${token}` } }
            ).then(() => {
                onThreadRead(thread); 
                setHasMarkedAsRead(true); 
                setTimeout(() => { if (refreshUnreadCount) refreshUnreadCount(); }, 500);
            }).catch(err => console.error("Failed to mark read:", err));
        }
    }, [thread, loading, hasMarkedAsRead, token, refreshUnreadCount, onThreadRead]);

    useEffect(() => { 
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }, [messages]);

    // 🚨 3. Early return comes AFTER all Hooks have been called!
    if (!thread) return (
        <Paper sx={{ p: 3, height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper' }}>
            <Box sx={{ textAlign: 'center', opacity: 0.6 }}>
                <SendIcon sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">Select a conversation</Typography>
            </Box>
        </Paper>
    );

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        event.target.value = null;
    };

    const handleSend = async () => {
        if ((!newMessage.trim() && !selectedFile) || sending) return;
        setSending(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('body', newMessage.trim());

            if (selectedFile) {
                formData.append('attachment', selectedFile); // Must match multerUpload.single('attachment')
            }
            if (thread.ApplicationId) formData.append('applicationId', thread.ApplicationId);
            if (thread.OtherUserId) formData.append('recipientId', thread.OtherUserId); 

            // 🚨 THE FIX: Remove Content-Type header. Let Axios/browser attach boundary automatically!
            await api.post('/messages/send', formData, { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            });

            setNewMessage('');
            setSelectedFile(null);

            if (!thread.isNewThread) await fetchMessages(); 
            onMessageSent(thread.isNewThread); 
        } catch (err) { 
            console.error("Failed to send:", err);
            setError('Failed to send message. Please try again.'); 
        } finally { 
            setSending(false); 
        }
    };

    const messageGroups = groupMessagesByDate(messages);

    return (
        <Paper sx={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
            {/* 🚨 UX UPGRADE: Sender Details Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'background.paper' }}>
                <IconButton onClick={onBack} sx={{ display: { md: 'none' } }}><ArrowBackIcon /></IconButton>
                <Avatar sx={{ bgcolor: 'primary.main', width: 45, height: 45 }}>{thread.ThreadTitle?.[0] || 'U'}</Avatar>
                <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 'bold' }}>
                        {thread.ThreadTitle || 'Unknown User'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip 
                            label={thread.ApplicationId ? `App Ref: ${thread.ApplicationId}` : (senderContext?.role || 'Direct Message')} 
                            size="small" 
                            color={thread.ApplicationId ? "secondary" : "default"} 
                            variant="outlined" 
                        />
                        {senderContext?.email && (
                            <Typography variant="caption" color="text.secondary">
                                {senderContext.email}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box ref={scrollContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: 'background.default', scrollBehavior: 'auto' }}>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
                {error && <Alert severity="error">{error}</Alert>}
                {thread.isNewThread && messages.length === 0 && <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>Start a new conversation.</Typography>}

                {messageGroups.map((item) => {
                    if (item.type === 'separator') return <DateSeparator key={item.id} date={item.date} />;
                    const msg = item.data;
                    const isSender = msg.SenderId === userId;

                    return (
                        <Box key={msg.Id} sx={{ display: 'flex', justifyContent: isSender ? 'flex-end' : 'flex-start', mb: 2 }}>
                            {!isSender && <Avatar sx={{ width: 28, height: 28, mr: 1, mt: 0.5, bgcolor: 'secondary.main', fontSize: '0.8rem' }}>{msg.SenderName?.[0]}</Avatar>}
                            <Box sx={{ maxWidth: '70%' }}>
                                <Paper elevation={isSender ? 2 : 1} sx={{ p: 1.5, bgcolor: isSender ? 'primary.main' : 'background.paper', color: isSender ? 'primary.contrastText' : 'text.primary', borderRadius: '16px', borderTopLeftRadius: isSender ? '16px' : '4px', borderTopRightRadius: isSender ? '4px' : '16px' }}>
                                    {msg.Body && (
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.95rem' }}>
                                            {renderMessageBody(msg.Body)}
                                        </Typography>
                                    )}
                                    {msg.AttachmentPath && (
                                        <Box sx={{ mt: msg.Body ? 1 : 0, p: 1, bgcolor: isSender ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachFileIcon fontSize="small" />
                                            <a href={msg.AttachmentPath} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all', fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                View Attachment
                                            </a>
                                        </Box>
                                    )}
                                </Paper>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isSender ? 'flex-end' : 'flex-start', mt: 0.5, px: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                        {getSafeDate(msg).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                    <ReadReceipt isSender={isSender} isRead={msg.IsRead} />
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {selectedFile && (
                <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                    <Chip icon={<AttachFileIcon />} label={selectedFile.name} onDelete={() => setSelectedFile(null)} deleteIcon={<CancelIcon />} />
                </Box>
            )}

            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', bgcolor: 'background.paper' }} component="form" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
                <Tooltip title="Attach File">
                    <IconButton color="primary" component="label" sx={{ mr: 1 }}><AttachFileIcon /><input type="file" hidden onChange={handleFileChange} /></IconButton>
                </Tooltip>
                <TextField 
    fullWidth 
    multiline // 🚨 Auto-expands input box height!
    maxRows={4} // Expands up to 4 lines before scrolling
    variant="outlined" 
    size="small" 
    placeholder="Type a message..." 
    value={newMessage} 
    onChange={(e) => setNewMessage(e.target.value)} 
    onKeyDown={(e) => {
        // Send on Enter, allow Shift+Enter for newlines
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }}
    disabled={sending} 
    sx={{ 
        '& .MuiOutlinedInput-root': { 
            borderRadius: 3, 
            bgcolor: 'background.default' 
        } 
    }} 
/>
                <IconButton color="primary" type="submit" disabled={(!newMessage.trim() && !selectedFile) || sending} sx={{ ml: 1, bgcolor: 'primary.light', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.main' } }}>
                    {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                </IconButton>
            </Box>
        </Paper>
    );
};

// --- NewConversationModal ---
const NewConversationModal = ({ open, onClose, token, onStartConversation }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (open) {
            setLoading(true);
            api.get('/messages/contacts', { headers: { Authorization: `Bearer ${token}` }})
                .then(res => setContacts(res.data))
                .catch(err => console.error("Failed", err)).finally(() => setLoading(false));
        }
    }, [open, token]);

    const filteredContacts = useMemo(() => contacts.filter(c => (c.FullName||'').toLowerCase().includes(searchTerm.toLowerCase())), [contacts, searchTerm]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>New Message</DialogTitle>
            <DialogContent sx={{ p: 2 }}>
                <TextField fullWidth variant="outlined" size="small" placeholder="Search people..." sx={{ mb: 2 }} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {loading ? <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box> : filteredContacts.map(contact => (
                        <ListItemButton key={contact.Id} onClick={() => { onStartConversation(contact); onClose(); }}>
                            <ListItemAvatar><Avatar sx={{ bgcolor: contact.Role === 'SuperUser' ? 'secondary.main' : 'primary.main' }}>{contact.FullName?.[0]}</Avatar></ListItemAvatar>
                            <ListItemText 
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {contact.FullName}
                                        {contact.Role === 'SuperUser' && <Chip label="Support" size="small" color="secondary" variant="filled" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }} />}
                                    </Box>
                                } 
                                secondary={contact.CompanyName || contact.Role} 
                            />
                        </ListItemButton>
                    ))}
                </List>
            </DialogContent>
        </Dialog>
    );
};

// --- Main Page Component ---
const MessageCenterPage = () => {
    const [serverThreads, setServerThreads] = useState([]);
    const [pendingThread, setPendingThread] = useState(null);
    const [selectedThread, setSelectedThread] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token, user, refreshUnreadCount } = useAuth();
    const [searchParams] = useSearchParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inboxSearch, setInboxSearch] = useState('');

    const fetchInbox = useCallback(async () => {
        if (!token) return;
        try {
            if (serverThreads.length === 0) setLoading(true);
            const response = await api.get('/messages/inbox', { headers: { Authorization: `Bearer ${token}` } });
            
            // 🚨 THE FIX: Map the aliases defensively so casing never breaks the UI
            const data = Array.isArray(response.data) ? response.data.map(t => ({
                ...t,
                ApplicationId: t.ApplicationId ?? t.applicationId ?? t.application_id,
                OtherUserId: t.OtherUserId ?? t.otherUserId ?? t.other_user_id,
                SenderId: t.SenderId ?? t.senderId ?? t.sender_id,
                RecipientId: t.RecipientId ?? t.recipientId ?? t.recipient_id
            })) : [];
            
            setServerThreads(data);
            
            if (refreshUnreadCount) refreshUnreadCount();
        } catch (err) { setError('Failed to load your inbox.'); } 
        finally { setLoading(false); }
    }, [token, serverThreads.length, refreshUnreadCount]);

    useEffect(() => { fetchInbox(); }, [fetchInbox]);

    const allThreads = useMemo(() => {
        if (pendingThread) {
            const exists = serverThreads.find(t => t.OtherUserId === pendingThread.OtherUserId && t.ApplicationId === pendingThread.ApplicationId);
            if (!exists) return [pendingThread, ...serverThreads];
        }
        return serverThreads;
    }, [serverThreads, pendingThread]);

    // 🚨 THE FIX: Map over the state array and explicitly override the unread count for the active chat
    const handleThreadRead = useCallback((readThread) => {
        setServerThreads(prevThreads => prevThreads.map(t => {
            if (t.ApplicationId === readThread.ApplicationId && t.OtherUserId === readThread.OtherUserId) {
                return { ...t, UnreadCount: 0, unreadCount: 0, unread_count: 0, unread: 0 };
            }
            return t;
        }));
    }, []);

    const handleSelectThread = useCallback((thread) => setSelectedThread(thread), []);

    useEffect(() => {
        const threadIdToOpen = searchParams.get('open_thread');
        if (threadIdToOpen && allThreads.length > 0) {
            const threadToSelect = allThreads.find(t => t.ApplicationId == threadIdToOpen);
            if (threadToSelect && threadToSelect.ApplicationId !== selectedThread?.ApplicationId) {
                handleSelectThread(threadToSelect);
            }
        }
    }, [allThreads, searchParams, handleSelectThread, selectedThread]);

    const handleStartConversation = (contact) => {
        const existingThread = serverThreads.find(t => t.OtherUserId === contact.Id && t.ApplicationId === null);
        if (existingThread) {
            setSelectedThread(existingThread);
            setPendingThread(null);
        } else {
            const newTempThread = {
                ApplicationId: null, OtherUserId: contact.Id, ThreadTitle: contact.FullName,
                OtherPartyName: contact.FullName, LastMessage: 'Start a new conversation...',
                CreatedAt: new Date().toISOString(), IsRead: 1, SenderId: user.id,
                UnreadCount: 0, isNewThread: true 
            };
            setPendingThread(newTempThread);
            setSelectedThread(newTempThread);
        }
    };

    const handleMessageSent = async (wasNewThread) => {
        if (wasNewThread) {
            setPendingThread(null);
            setSelectedThread(null); 
        }
        await fetchInbox();
    };

    const filteredThreadsList = useMemo(() =>
        allThreads.filter(thread =>
            (thread.ThreadTitle && thread.ThreadTitle.toLowerCase().includes(inboxSearch.toLowerCase())) ||
            (thread.LastMessage && thread.LastMessage.toLowerCase().includes(inboxSearch.toLowerCase()))
        ),
    [allThreads, inboxSearch]);

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, height: 'calc(100vh - 100px)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">Message Center</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsModalOpen(true)}>New Message</Button>
            </Box>

            <Grid container spacing={2} sx={{ height: '100%' }}>
                <Grid item xs={12} md={4} sx={{ display: { xs: selectedThread ? 'none' : 'block', md: 'block' }, height: '100%' }}>
                    <Paper sx={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <TextField fullWidth variant="outlined" size="small" placeholder="Search inbox..." value={inboxSearch} onChange={(e) => setInboxSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            {loading && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress /></Box>}
                            {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
                            <List sx={{ p: 0 }}>
                                {filteredThreadsList.map((thread) => {
                                    const unreadCount = getSafeUnreadCount(thread);
                                    const isUnread = unreadCount > 0;
                                    const key = thread.isNewThread ? `new-${thread.OtherUserId}` : (thread.ApplicationId ? `app-${thread.ApplicationId}` : `user-${thread.OtherUserId}`);

                                    const isSelected = selectedThread && (
                                        selectedThread.OtherUserId === thread.OtherUserId && 
                                        selectedThread.ApplicationId === thread.ApplicationId
                                    );

                                    return (
                                        <React.Fragment key={key}>
                                            <ListItemButton 
                                                selected={Boolean(isSelected)} 
                                                onClick={() => handleSelectThread(thread)}
                                                sx={{ borderLeft: isSelected ? '4px solid' : '4px solid transparent', borderColor: 'primary.main', bgcolor: isSelected ? 'action.selected' : 'transparent' }}
                                            >
                                                <ListItemAvatar><Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.400' }}>{thread.ThreadTitle ? thread.ThreadTitle[0] : 'U'}</Avatar></ListItemAvatar>
                                                <ListItemText 
                                                    primary={thread.ThreadTitle} 
                                                    primaryTypographyProps={{ fontWeight: isUnread ? '800' : '500', noWrap: true, sx: { maxWidth: '80%' } }} 
                                                    secondary={<Typography component="span" variant="body2" noWrap sx={{ color: isUnread ? 'text.primary' : 'text.secondary', fontWeight: isUnread ? '600' : '400', display: 'block', maxWidth: '85%' }}>{thread.SenderId === user.id && 'You: '}{thread.LastMessage}</Typography>} 
                                                />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1, flexShrink: 0 }}>
                                                    <Typography variant="caption" sx={{ mb: 0.5, color: isUnread ? 'primary.main' : 'text.secondary', fontWeight: isUnread ? 'bold' : 'normal' }}>
                                                        {formatTimestamp(getSafeDate(thread))}
                                                    </Typography>
                                                    {isUnread && <Chip label={unreadCount} color="error" size="small" sx={{ height: 20, minWidth: 20, fontSize: '0.75rem', fontWeight: 'bold', mr: 1, mt: 0.5 }} />}
                                                </Box>
                                            </ListItemButton>
                                            <Divider component="li" variant="inset" />
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={8} sx={{ display: { xs: selectedThread ? 'block' : 'none', md: 'block' }, height: '100%' }}>
                    <ChatView thread={selectedThread} token={token} userId={user?.id} onMessageSent={handleMessageSent} refreshUnreadCount={refreshUnreadCount} onThreadRead={handleThreadRead} onBack={() => setSelectedThread(null)} />
                </Grid>
            </Grid>
            <NewConversationModal open={isModalOpen} onClose={() => setIsModalOpen(false)} token={token} onStartConversation={handleStartConversation} />
        </Container>
    );
};

export default MessageCenterPage;
